<template>
  <modal name="modal-gerar-formula-ia" :adaptive="true" height="auto" :maxWidth="640" :clickToClose="!carregando">
    <div class="mgi-root">
      <!-- INPUT -->
      <template v-if="estado === 'input'">
        <h3 class="mgi-title"><fa-icon icon="magic" /> Gerar Fórmula com IA</h3>
        <p class="mgi-desc">Descreva a fórmula desejada ou anexe um documento com as regras de cálculo.</p>

        <textarea
          v-model="descricao"
          class="mgi-textarea"
          placeholder="Ex: Calcule a margem descontando ICMS de 18%, PIS de 1.65%, COFINS de 7.60% e custo financeiro do preço com imposto..."
          rows="4"
        ></textarea>

        <div class="mgi-actions-row">
          <label class="mgi-attach-btn" title="Anexar documento ou imagem">
            <fa-icon icon="paperclip" />
            <span v-if="arquivos.length === 0">Anexar</span>
            <span v-else>{{ arquivos.length }} arquivo(s)</span>
            <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp" multiple @change="onArquivos" hidden />
          </label>
        </div>

        <div class="mgi-chips" v-if="arquivos.length > 0">
          <div class="mgi-chip" v-for="(f, i) in arquivos" :key="i">
            <span>{{ f.name }}</span>
            <button @click="removerArquivo(i)"><fa-icon icon="times" /></button>
          </div>
        </div>

        <div class="mgi-footer">
          <button class="mgi-btn mgi-btn-cancel" @click="fechar">Cancelar</button>
          <button class="mgi-btn mgi-btn-gerar" :disabled="!temInput" @click="gerar">
            <fa-icon icon="magic" /> Gerar com IA
          </button>
        </div>
      </template>

      <!-- LOADING -->
      <template v-if="estado === 'loading'">
        <div class="mgi-loading">
          <fa-icon icon="spinner" spin class="mgi-loading-icon" />
          <h3>Gerando fórmula...</h3>
          <p>A IA está analisando sua descrição e montando as etapas de cálculo.</p>
        </div>
      </template>

      <!-- PREVIEW -->
      <template v-if="estado === 'preview'">
        <h3 class="mgi-title"><fa-icon icon="check-circle" class="mgi-success-icon" /> Fórmula Gerada</h3>
        <div class="mgi-preview-info">
          <strong>{{ resultado.titulo }}</strong>
          <p v-if="resultado.descricao">{{ resultado.descricao }}</p>
        </div>

        <div class="mgi-preview-linhas">
          <template v-for="(l, li) in resultado.linhas">
            <div :key="'row' + l.ordem" class="mgi-node-row">
              <div class="mgi-node mgi-node-result" :title="l.resultado">{{ getInitials(l.resultado) }}</div>
              <div class="mgi-wire"></div>
              <div class="mgi-node mgi-node-eq">=</div>
              <div class="mgi-wire"></div>
              <div
                v-for="(tk, ti) in tokenizar(l.expressao)"
                :key="ti"
                class="mgi-node-wrap"
              >
                <div class="mgi-node" :class="nodeClass(tk)" :title="nodeTitle(tk)">{{ nodeLabel(tk) }}</div>
                <div v-if="ti < tokenizar(l.expressao).length - 1" class="mgi-wire"></div>
              </div>
            </div>
            <div v-if="li < resultado.linhas.length - 1" :key="'vw' + l.ordem" class="mgi-vwire"></div>
          </template>
        </div>

        <div class="mgi-footer">
          <button class="mgi-btn mgi-btn-cancel" @click="estado = 'input'">
            <fa-icon icon="redo" /> Tentar novamente
          </button>
          <button class="mgi-btn mgi-btn-usar" @click="usarFormula">
            <fa-icon icon="check" /> Usar esta fórmula
          </button>
        </div>
      </template>

      <!-- ERRO -->
      <template v-if="estado === 'erro'">
        <div class="mgi-loading">
          <fa-icon icon="exclamation-triangle" class="mgi-erro-icon" />
          <h3>Erro ao gerar</h3>
          <p>{{ erroMsg }}</p>
          <div class="mgi-footer">
            <button class="mgi-btn mgi-btn-cancel" @click="estado = 'input'">Tentar novamente</button>
          </div>
        </div>
      </template>
    </div>
  </modal>
</template>

<script>
import { post } from "@/helpers/apiRequest";

export default {
  name: "ModalGerarFormulaIA",
  props: {
    acao: { type: String, default: "margem" },
  },
  data() {
    return {
      estado: "input",
      descricao: "",
      arquivos: [],
      resultado: null,
      erroMsg: "",
      carregando: false,
    };
  },
  computed: {
    temInput() {
      return this.descricao.trim().length > 0 || this.arquivos.length > 0;
    },
  },
  methods: {
    onArquivos(e) {
      const novos = Array.from(e.target.files).slice(0, 3 - this.arquivos.length);
      this.arquivos.push(...novos);
      e.target.value = "";
    },
    removerArquivo(i) {
      this.arquivos.splice(i, 1);
    },
    async gerar() {
      this.estado = "loading";
      this.carregando = true;

      try {
        const formData = new FormData();
        formData.append("acao", this.acao);
        if (this.descricao.trim()) formData.append("descricao", this.descricao.trim());
        this.arquivos.forEach((f, i) => formData.append(`arquivos[${i}]`, f));

        const res = await post("/admin/formulas-calculo/gerar-com-ia", formData, { isFile: true });
        this.resultado = res.data || res;
        this.estado = "preview";
      } catch (e) {
        this.erroMsg = e?.message || e?.error || "Erro inesperado ao gerar a fórmula.";
        if (this.erroMsg === "Failed to fetch") {
          this.erroMsg = "Tempo limite excedido ou erro de conexão. Tente novamente com uma descrição mais curta.";
        }
        this.estado = "erro";
      } finally {
        this.carregando = false;
      }
    },
    tokenizar(expr) {
      if (!expr) return [];
      const tokens = [];
      const ops = { "+": "Soma", "-": "Subtração", "*": "Multiplicação", "/": "Divisão" };
      let pos = 0;
      const len = expr.length;
      while (pos < len) {
        const ch = expr[pos];
        if (/\s/.test(ch)) { pos++; continue; }
        if ("()".includes(ch)) { tokens.push({ tipo: "paren", val: ch }); pos++; continue; }
        if ("+-*/".includes(ch)) { tokens.push({ tipo: "op", val: ch, label: ops[ch] }); pos++; continue; }
        if (/[\d.]/.test(ch)) {
          let n = "";
          while (pos < len && /[\d.]/.test(expr[pos])) { n += expr[pos]; pos++; }
          tokens.push({ tipo: "num", val: n });
          continue;
        }
        if (/[a-zA-Z_]/.test(ch)) {
          let v = "";
          while (pos < len && /[a-zA-Z0-9_]/.test(expr[pos])) { v += expr[pos]; pos++; }
          tokens.push({ tipo: "var", val: v });
          continue;
        }
        pos++;
      }
      return tokens;
    },
    nodeClass(tk) {
      if (tk.tipo === "var") return "mgi-node-var";
      if (tk.tipo === "op") return "mgi-node-op";
      if (tk.tipo === "num") return "mgi-node-num";
      if (tk.tipo === "paren") return "mgi-node-paren";
      return "";
    },
    nodeLabel(tk) {
      if (tk.tipo === "op") return { "+": "+", "-": "−", "*": "×", "/": "÷" }[tk.val] || tk.val;
      if (tk.tipo === "var") return this.getInitials(tk.val);
      return tk.val;
    },
    nodeTitle(tk) {
      if (tk.tipo === "var") return tk.val.replace(/^fcd_/, "").replace(/_/g, " ");
      if (tk.tipo === "op") return { "+": "Soma", "-": "Subtração", "*": "Multiplicação", "/": "Divisão" }[tk.val] || tk.val;
      if (tk.tipo === "num") return "Valor " + tk.val;
      return tk.val;
    },
    getInitials(nome) {
      const clean = nome.replace(/^fcd_/, "").replace(/_/g, " ");
      const words = clean.split(/\s+/).filter(Boolean);
      const stops = ["de", "do", "da", "dos", "das"];
      const meaningful = words.filter((w) => !stops.includes(w.toLowerCase()));
      if (meaningful.length >= 2) return (meaningful[0][0] + meaningful[1][0]).toUpperCase();
      if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
      return clean.substring(0, 2).toUpperCase();
    },
    usarFormula() {
      sessionStorage.setItem("formula_ia_data", JSON.stringify(this.resultado));
      this.$modal.hide("modal-gerar-formula-ia");
      this.$emit("gerada", this.resultado);
    },
    fechar() {
      this.$modal.hide("modal-gerar-formula-ia");
    },
  },
};
</script>

<style scoped>
.mgi-root { padding: 28px; }
.mgi-title { font-size: 1.1rem; font-weight: 700; color: #1f2937; margin: 0 0 6px; display: flex; align-items: center; gap: 8px; }
.mgi-desc { font-size: 0.8rem; color: #9ca3af; margin: 0 0 16px; line-height: 1.5; }

.mgi-textarea {
  width: 100%; padding: 10px 12px; border: 1.5px solid #e5e7eb; border-radius: 10px;
  font-size: 0.82rem; resize: vertical; outline: none; font-family: inherit;
  min-height: 80px;
}
.mgi-textarea:focus { border-color: #818cf8; }
.mgi-textarea::placeholder { color: #d1d5db; }

.mgi-actions-row { display: flex; align-items: center; gap: 12px; margin-top: 12px; }

.mgi-attach-btn {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 14px; border-radius: 20px;
  background: #f3f4f6; color: #6b7280; border: 1px solid #e5e7eb;
  font-size: 0.75rem; cursor: pointer; transition: all 0.15s;
}
.mgi-attach-btn:hover { background: #e5e7eb; color: #374151; }

.mgi-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.mgi-chip {
  display: flex; align-items: center; gap: 4px;
  padding: 3px 10px; border-radius: 16px;
  background: #f0f4ff; border: 1px solid #c7d2fe;
  font-size: 0.7rem; color: #4338ca;
}
.mgi-chip button { background: none; border: none; color: #6366f1; cursor: pointer; font-size: 0.6rem; }

.mgi-footer { display: flex; justify-content: flex-end; gap: 10px; margin-top: 20px; }

.mgi-btn {
  padding: 8px 18px; border-radius: 8px; border: none;
  font-size: 0.82rem; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; gap: 6px; transition: all 0.15s;
}
.mgi-btn-cancel { background: #f3f4f6; color: #6b7280; }
.mgi-btn-cancel:hover { background: #e5e7eb; }
.mgi-btn-gerar { background: #7c3aed; color: #fff; }
.mgi-btn-gerar:hover { background: #6d28d9; }
.mgi-btn-gerar:disabled { opacity: 0.5; cursor: not-allowed; }
.mgi-btn-usar { background: #059669; color: #fff; }
.mgi-btn-usar:hover { background: #047857; }

.mgi-loading { text-align: center; padding: 40px 20px; }
.mgi-loading-icon { font-size: 2rem; color: #7c3aed; margin-bottom: 12px; }
.mgi-loading h3 { font-size: 1rem; color: #374151; margin: 0 0 6px; }
.mgi-loading p { font-size: 0.8rem; color: #9ca3af; margin: 0; }
.mgi-erro-icon { font-size: 2rem; color: #ef4444; margin-bottom: 12px; }
.mgi-success-icon { color: #059669; }

.mgi-preview-info { margin-bottom: 16px; }
.mgi-preview-info strong { font-size: 0.9rem; color: #374151; }
.mgi-preview-info p { font-size: 0.78rem; color: #9ca3af; margin: 4px 0 0; }

.mgi-preview-linhas {
  display: flex; flex-direction: column; align-items: center;
  max-height: 320px; overflow-y: auto; padding: 8px 0;
}

.mgi-node-row {
  display: flex; align-items: center; flex-wrap: wrap; justify-content: center; gap: 0;
}
.mgi-node-wrap { display: flex; align-items: center; }

.mgi-node {
  width: 36px; height: 36px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.5rem; font-weight: 700; flex-shrink: 0;
  border: 1.5px solid #e5e7eb; background: #fff; color: #374151;
  text-align: center; overflow: hidden; padding: 2px;
}
.mgi-node-result {
  border-color: #c7d2fe; background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  color: #4338ca;
}
.mgi-node-eq { border-color: #d1d5db; background: #f9fafb; color: #9ca3af; font-size: 0.7rem; width: 28px; height: 28px; }
.mgi-node-var {
  border-color: #a7f3d0; background: linear-gradient(135deg, #ecfdf5, #d1fae5); color: #065f46;
}
.mgi-node-op { border-color: #ddd6fe; background: linear-gradient(135deg, #f5f3ff, #ede9fe); color: #7c3aed; font-size: 0.75rem; width: 28px; height: 28px; }
.mgi-node-num { border-color: #fde68a; background: linear-gradient(135deg, #fefce8, #fef3c7); color: #92400e; font-family: 'SF Mono', monospace; font-size: 0.55rem; width: auto; min-width: 28px; padding: 0 4px; }
.mgi-node-paren { border-color: #d1d5db; background: #f3f4f6; color: #6b7280; font-size: 0.7rem; width: 24px; height: 28px; }

.mgi-wire { width: 12px; height: 1.5px; background: #d1d5db; flex-shrink: 0; }
.mgi-vwire { width: 1.5px; height: 12px; background: #d1d5db; margin: 2px auto; }
</style>
